import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import getAsyncStatus, { AsyncOperationStatus } from "../http/getAsyncStatus";
import { isTerminalOperationStatus } from "../models/operationStatus";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

const POLL_INTERVAL_MS = 3000;
const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

interface UseAsyncOperationParams {
  siteId: string;
  operationKey: string | null;
  enabled?: boolean;
}

export const useAsyncOperation = ({
  siteId,
  operationKey,
  enabled = true,
}: UseAsyncOperationParams) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const [timedOut, setTimedOut] = useState(false);
  const startTimeRef = useRef<number | null>(null);

  const isActive = enabled && !!operationKey && !timedOut;

  // Track start time
  useEffect(() => {
    if (isActive && startTimeRef.current === null) {
      startTimeRef.current = Date.now();
    }
    if (!isActive) {
      startTimeRef.current = null;
      setTimedOut(false);
    }
  }, [isActive]);

  const query = useQuery<AsyncOperationStatus>(
    ["async_status", siteId, operationKey],
    async () => {
      const pastDeadline = () =>
        startTimeRef.current !== null &&
        Date.now() - startTimeRef.current > TIMEOUT_MS;
      // Polling pauses while the tab is hidden, so the first poll after the
      // operator returns can be past the deadline for an operation that
      // finished long ago: read the status before deciding it timed out.
      let status: AsyncOperationStatus;
      try {
        status = await getAsyncStatus(
          await getAuthTokenBlocking(getToken, relogin),
          siteId,
          operationKey!
        );
      } catch (error) {
        if (pastDeadline()) setTimedOut(true);
        throw error;
      }
      const inFlight = !isTerminalOperationStatus(status.status);
      if (inFlight && pastDeadline()) {
        setTimedOut(true);
        throw new Error("Operation timed out after 15 minutes");
      }
      return status;
    },
    {
      enabled: isActive,
      refetchInterval: (data) => {
        if (!data) return POLL_INTERVAL_MS;
        if (!isTerminalOperationStatus(data.status)) return POLL_INTERVAL_MS;
        return false;
      },
      // A settled status can't change, and its row expires after 24h, so a reconnect
      // after a long-open tab must not re-ask for it.
      refetchOnReconnect: (query) =>
        !isTerminalOperationStatus(query.state.data?.status),
      staleTime: 0,
      cacheTime: 0,
    }
  );

  return {
    ...query,
    timedOut,
    isPolling: isActive && !isTerminalOperationStatus(query.data?.status),
  };
};
